import { format, parseISO } from 'date-fns'
import { tr } from 'date-fns/locale'
import { useMemo } from 'react'
import { useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { apiErrorMessage } from '@/i18n'
import { useAccounts, useCategories, useParties, useTransactions } from './api'

export function toastError(e: unknown) {
  toast.error(apiErrorMessage(e))
}

/** Dönem seçicileri: içinde bulunulan ay dahil son 12 ay. */
export const FINANCE_MONTHS: readonly string[] = Array.from({ length: 12 }, (_, i) => {
  const now = new Date()
  return format(new Date(now.getFullYear(), now.getMonth() - 11 + i, 1), 'yyyy-MM')
})
export const DEFAULT_MONTH: string = FINANCE_MONTHS[FINANCE_MONTHS.length - 1] ?? format(new Date(), 'yyyy-MM')

/** Dönem (ay) URL'de `?m=` olarak tutulur; finans sayfaları arasında korunur. */
export function usePeriod() {
  const [params, setParams] = useSearchParams()
  const raw = params.get('m') ?? ''
  const month = FINANCE_MONTHS.includes(raw) ? raw : DEFAULT_MONTH
  const setMonth = (m: string) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('m', m)
        return next
      },
      { replace: true },
    )
  return { month, setMonth }
}

export const monthLabel = (m: string, pattern = 'LLLL yyyy') => format(parseISO(`${m}-01`), pattern, { locale: tr })
export const monthShort = (m: string) => format(parseISO(`${m}-01`), 'LLL yyyy', { locale: tr })

/** Kategori, cari, hesap ve kayıt adları için tek yerden arama tabloları. */
export function useFinanceLookups() {
  const categories = useCategories()
  const parties = useParties()
  const accounts = useAccounts()
  const transactions = useTransactions()
  return useMemo(() => {
    const cat = new Map((categories.data ?? []).map((c) => [c.id, c]))
    const party = new Map((parties.data ?? []).map((p) => [p.id, p]))
    const account = new Map((accounts.data ?? []).map((a) => [a.id, a]))
    const tx = new Map((transactions.data ?? []).map((x) => [x.id, x]))
    return {
      categories: categories.data ?? [],
      parties: parties.data ?? [],
      accounts: accounts.data ?? [],
      cat,
      party,
      account,
      tx,
      catName: (id: string) => cat.get(id)?.name ?? '—',
      partyName: (id: string) => party.get(id)?.name ?? '—',
      accountName: (id: string) => account.get(id)?.name ?? '—',
      /** "Garanti BBVA · Mastercard •••• 7744" gibi; tam numara asla gösterilmez. */
      accountDetail: (id: string) => {
        const a = account.get(id)
        if (!a) return ''
        if (a.type === 'credit_card') return [a.bankName, `${a.cardBrand ? a.cardBrand.toUpperCase() + ' ' : ''}•••• ${a.cardLast4}`].filter(Boolean).join(' · ')
        return [a.bankName, a.ibanMasked ? `•••• ${a.ibanMasked.slice(-4)}` : ''].filter(Boolean).join(' · ')
      },
    }
  }, [categories.data, parties.data, accounts.data, transactions.data])
}
